import React, { useState, useEffect, useRef } from 'react';
import { representanteAPI } from '../../services/api';
import PhotoCapture from '../../components/PhotoCapture';

const API_URL = process.env.REACT_APP_API_URL || 'https://lossaliasfutbolclub.com/api';
const UPLOADS_URL = `${API_URL.replace('/api', '')}/uploads/documentos`;

const docLabels = {
  cedula_alumno: { label: 'Cédula del Alumno', icon: '🪪' },
  partida_nacimiento: { label: 'Partida de Nacimiento', icon: '📜' },
  cedula_padre: { label: 'Cédula del Padre', icon: '🪪' },
  cedula_madre: { label: 'Cédula de la Madre', icon: '🪪' },
};

const JugadorDetalle = ({ jugadorId, onBack }) => {
  const [jugador, setJugador] = useState(null);
  const [documentos, setDocumentos] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null); // which doc type is uploading
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileInputRef = useRef(null);
  const uploadTypeRef = useRef(null);

  useEffect(() => {
    loadDetalle();
  }, [jugadorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDetalle = async () => {
    try {
      setLoading(true);
      const res = await representanteAPI.getJugadorDetalle(jugadorId);
      setJugador(res.data.jugador);
      setDocumentos(res.data.documentos || {});
    } catch (err) {
      console.error('Error loading player detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = (tipo) => {
    uploadTypeRef.current = tipo;
    setUploadError('');
    setUploadSuccess('');
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const tipo = uploadTypeRef.current;
    if (!tipo) return;

    // Validate file size
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('El archivo excede el tamaño máximo de 5MB');
      return;
    }

    setUploading(tipo);
    setUploadError('');
    setUploadSuccess('');

    try {
      const formData = new FormData();
      formData.append('documento', file);
      formData.append('tipo', tipo);
      await representanteAPI.uploadDocumento(jugadorId, formData);
      setUploadSuccess(`${docLabels[tipo]?.label || tipo} actualizado correctamente`);
      // Reload to get updated document URLs
      await loadDetalle();
    } catch (err) {
      console.error('Error uploading document:', err);
      setUploadError(err.response?.data?.error || 'Error al subir el documento');
    } finally {
      setUploading(null);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePhotoReady = async (blob) => {
    const formData = new FormData();
    formData.append('foto', blob, 'foto_jugador.jpg');
    await representanteAPI.uploadFoto(jugadorId, formData);
    await loadDetalle();
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-lsfc-green-600"></div>
      </div>
    );
  }

  if (!jugador) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No se pudo cargar la información del jugador</p>
        <button onClick={onBack} className="mt-4 text-lsfc-green-600 font-medium">← Volver</button>
      </div>
    );
  }

  const j = jugador;

  // Determine which doc types to show
  const docTypes = ['cedula_alumno', 'partida_nacimiento'];
  if (j.padre_id) docTypes.push('cedula_padre');
  if (j.madre_id) docTypes.push('cedula_madre');

  return (
    <div className="space-y-4">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Header with back button */}
      <div className="flex items-center gap-3 print:hidden">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600">
          ←
        </button>
        <h2 className="text-lg font-bold text-gray-800 flex-1">Datos del Jugador</h2>
        <button
          onClick={handlePrint}
          className="px-3 py-1.5 bg-lsfc-blue-600 text-white rounded-lg text-xs hover:bg-lsfc-blue-700 flex items-center gap-1"
        >
          🖨️ Imprimir Planilla
        </button>
      </div>

      {/* ===== PRINTABLE AREA ===== */}
      <div className="print-area">

        {/* Print header (only visible when printing) */}
        <div className="hidden print:block mb-6 text-center border-b-2 border-gray-800 pb-4">
          <div className="flex items-center justify-center gap-4 mb-2">
            <img src="/logo_lsfc.png" alt="Los Salias FC" className="w-20 h-20 object-contain" />
            <div>
              <h1 className="text-2xl font-bold">Los Salias Fútbol Club</h1>
              <h2 className="text-lg font-semibold">Planilla de Inscripción</h2>
            </div>
            <img src="/logo_lsfc.png" alt="Los Salias FC" className="w-20 h-20 object-contain" />
          </div>
          <p className="text-sm text-gray-600 mt-1">Fecha de impresión: {new Date().toLocaleDateString('es-VE')}</p>
        </div>

        {/* Player photo + basic info */}
        <div className="bg-white rounded-xl shadow-sm p-4 print:shadow-none print:border print:border-gray-300">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center print:border print:border-gray-400">
              {j.foto_url ? (
                <img
                  src={`${UPLOADS_URL}/${j.foto_url}`}
                  alt={j.nombres}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <span className="text-4xl">⚽</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-gray-800">{j.nombres} {j.apellidos}</h3>
              <p className="text-sm text-gray-500">C.I.: {j.cedula}</p>
              <div className="flex gap-2 mt-1 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{j.edad} años</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{j.sexo === 'M' ? 'Masculino' : 'Femenino'}</span>
                {j.categoria && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-indigo-100 text-indigo-800">
                    {j.categoria}
                  </span>
                )}
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  j.status === 'media_beca' ? 'bg-amber-100 text-amber-700' :
                  j.status === 'becado' ? 'bg-green-100 text-green-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {j.status === 'media_beca' ? '🎓 Media Beca' : j.status === 'becado' ? '🎓 Becado' : 'Regular'}
                </span>
              </div>
            </div>
          </div>
          {/* Photo capture button */}
          <div className="mt-3 pt-3 border-t print:hidden">
            <PhotoCapture
              currentPhotoUrl={j.foto_url ? `${UPLOADS_URL}/${j.foto_url}` : null}
              onPhotoReady={handlePhotoReady}
              playerName={j.nombres}
            />
          </div>
        </div>

        {/* Personal data */}
        <div className="bg-white rounded-xl shadow-sm p-4 mt-3 print:shadow-none print:border print:border-gray-300">
          <h4 className="font-bold text-gray-700 mb-3 text-sm border-b pb-2">📋 Datos Personales</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <InfoField label="Fecha de Nacimiento" value={j.fecha_nacimiento ? new Date(j.fecha_nacimiento + 'T12:00:00').toLocaleDateString('es-VE') : '-'} />
            <InfoField label="Lugar de Nacimiento" value={j.lugar_nacimiento} />
            <InfoField label="Estado" value={j.estado} />
            <InfoField label="País" value={j.pais || 'Venezuela'} />
            <InfoField label="Teléfono" value={j.telefono_celular} />
            <InfoField label="Colegio" value={j.colegio_actual} />
            <InfoField label="Dirección" value={j.direccion_habitacion} full />
          </div>
        </div>

        {/* Parents info */}
        {(j.padre_nombre || j.madre_nombre) && (
          <div className="bg-white rounded-xl shadow-sm p-4 mt-3 print:shadow-none print:border print:border-gray-300">
            <h4 className="font-bold text-gray-700 mb-3 text-sm border-b pb-2">👨‍👩‍👦 Representantes</h4>
            <div className="space-y-3">
              {j.padre_nombre && (
                <div className="p-3 bg-blue-50 rounded-lg print:bg-white print:border print:border-gray-200">
                  <p className="text-xs font-bold text-blue-600 mb-1">👨 Padre</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <InfoField label="Nombre" value={`${j.padre_nombre} ${j.padre_apellido}`} />
                    <InfoField label="Cédula" value={j.padre_cedula} />
                    <InfoField label="Teléfono" value={j.padre_telefono} />
                    <InfoField label="Email" value={j.padre_email} />
                  </div>
                </div>
              )}
              {j.madre_nombre && (
                <div className="p-3 bg-pink-50 rounded-lg print:bg-white print:border print:border-gray-200">
                  <p className="text-xs font-bold text-pink-600 mb-1">👩 Madre</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <InfoField label="Nombre" value={`${j.madre_nombre} ${j.madre_apellido}`} />
                    <InfoField label="Cédula" value={j.madre_cedula} />
                    <InfoField label="Teléfono" value={j.madre_telefono} />
                    <InfoField label="Email" value={j.madre_email} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Medical info */}
        <div className="bg-white rounded-xl shadow-sm p-4 mt-3 print:shadow-none print:border print:border-gray-300">
          <h4 className="font-bold text-gray-700 mb-3 text-sm border-b pb-2">🏥 Información Médica</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <InfoField label="Tipo de Sangre" value={j.tipo_sangre ? `${j.grupo_sangre || ''}${j.tipo_sangre}` : '-'} />
            <InfoField label="Seguro Médico" value={j.tiene_seguro ? `Sí - ${j.compania_seguro || ''}` : 'No'} />
            {j.tiene_seguro && <InfoField label="Nro. Póliza" value={j.numero_poliza} />}
            <InfoField label="Padece Enfermedades" value={j.padece_enfermedades ? `Sí - ${j.tipo_enfermedades || ''}` : 'No'} />
            <InfoField label="Alergias" value={j.alergias || 'Ninguna'} full />
          </div>
        </div>

        {/* Emergency contacts */}
        {(j.emergencia1_nombre || j.emergencia2_nombre) && (
          <div className="bg-white rounded-xl shadow-sm p-4 mt-3 print:shadow-none print:border print:border-gray-300">
            <h4 className="font-bold text-gray-700 mb-3 text-sm border-b pb-2">🚨 Contactos de Emergencia</h4>
            <div className="space-y-3">
              {j.emergencia1_nombre && (
                <div className="p-3 bg-red-50 rounded-lg print:bg-white print:border print:border-gray-200">
                  <p className="text-xs font-bold text-red-600 mb-1">Contacto 1</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <InfoField label="Nombre" value={j.emergencia1_nombre} />
                    <InfoField label="Parentesco" value={j.emergencia1_parentesco} />
                    <InfoField label="Cédula" value={j.emergencia1_cedula} />
                    <InfoField label="Teléfono" value={j.emergencia1_telefono} />
                  </div>
                </div>
              )}
              {j.emergencia2_nombre && (
                <div className="p-3 bg-orange-50 rounded-lg print:bg-white print:border print:border-gray-200">
                  <p className="text-xs font-bold text-orange-600 mb-1">Contacto 2</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <InfoField label="Nombre" value={j.emergencia2_nombre} />
                    <InfoField label="Parentesco" value={j.emergencia2_parentesco} />
                    <InfoField label="Cédula" value={j.emergencia2_cedula} />
                    <InfoField label="Teléfono" value={j.emergencia2_telefono} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Authorizations */}
        <div className="bg-white rounded-xl shadow-sm p-4 mt-3 print:shadow-none print:border print:border-gray-300">
          <h4 className="font-bold text-gray-700 mb-3 text-sm border-b pb-2">✅ Autorizaciones</h4>
          <div className="space-y-2 text-sm">
            <AuthField label="Autorización médica en caso de emergencia" value={j.autorizacion_medica} />
            <AuthField label="Autorización de uso de imagen" value={j.autorizacion_imagen} />
            <AuthField label="Autorización de suministro de medicamentos" value={j.autorizacion_medicamentos} />
          </div>
        </div>

        {/* Signature area (print only) */}
        <div className="hidden print:block mt-10">
          <div className="grid grid-cols-2 gap-8 mt-16">
            <div className="text-center border-t border-gray-400 pt-2">
              <p className="text-sm font-medium">Firma del Representante</p>
              <p className="text-xs text-gray-500">C.I.: _______________</p>
            </div>
            <div className="text-center border-t border-gray-400 pt-2">
              <p className="text-sm font-medium">Firma del Director</p>
              <p className="text-xs text-gray-500">Sello</p>
            </div>
          </div>
        </div>

      </div>
      {/* ===== END PRINTABLE AREA ===== */}

      {/* Documents section (NOT printed - interactive) */}
      <div className="bg-white rounded-xl shadow-sm p-4 mt-3 print:hidden">
        <h4 className="font-bold text-gray-700 mb-3 text-sm border-b pb-2">📎 Documentos</h4>

        {uploadError && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {uploadError}
          </div>
        )}

        {uploadSuccess && (
          <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600">
            ✅ {uploadSuccess}
          </div>
        )}

        <div className="space-y-3">
          {docTypes.map((tipo) => {
            const doc = documentos[tipo];
            const info = docLabels[tipo];
            const isUploading = uploading === tipo;

            return (
              <div key={tipo} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <span className="text-xl">{info?.icon || '📄'}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{info?.label || tipo}</p>
                  {doc ? (
                    <p className="text-xs text-green-600">✅ Documento cargado</p>
                  ) : (
                    <p className="text-xs text-amber-600">⚠️ No cargado</p>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {doc && (
                    <button
                      onClick={() => setPreviewDoc({ tipo, url: `${UPLOADS_URL}/${doc.url}` })}
                      className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200"
                    >
                      👁️ Ver
                    </button>
                  )}
                  <button
                    onClick={() => handleUploadClick(tipo)}
                    disabled={isUploading}
                    className="px-2 py-1 bg-lsfc-green-100 text-lsfc-green-700 rounded-lg text-xs font-medium hover:bg-lsfc-green-200 disabled:opacity-50"
                  >
                    {isUploading ? (
                      <span className="flex items-center gap-1">
                        <span className="animate-spin inline-block w-3 h-3 border-b-2 border-lsfc-green-600 rounded-full"></span>
                        Subiendo...
                      </span>
                    ) : doc ? '🔄 Cambiar' : '📤 Subir'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Document preview modal */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4 print:hidden">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-gray-800">{docLabels[previewDoc.tipo]?.label}</h3>
              <button
                onClick={() => setPreviewDoc(null)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <img
                src={previewDoc.url}
                alt={previewDoc.tipo}
                className="w-full rounded-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<p class="text-center text-gray-500 py-8">No se pudo cargar la imagen. Puede ser un PDF u otro formato.</p>';
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:border { border: 1px solid #d1d5db !important; }
          .print\\:border-gray-300 { border-color: #d1d5db !important; }
          .print\\:border-gray-200 { border-color: #e5e7eb !important; }
          .print\\:border-gray-400 { border-color: #9ca3af !important; }
          .print\\:bg-white { background-color: white !important; }
        }
      `}</style>
    </div>
  );
};

// Helper components
const InfoField = ({ label, value, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <p className="text-xs text-gray-400">{label}</p>
    <p className="text-gray-800 font-medium">{value || '-'}</p>
  </div>
);

const AuthField = ({ label, value }) => (
  <div className="flex items-center gap-2">
    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
      value ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
    }`}>
      {value ? '✓' : '✕'}
    </span>
    <span className="text-gray-700">{label}</span>
  </div>
);

export default JugadorDetalle;
